import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useSignedImage } from '@/hooks/useSignedImage';
import type { Difficulty, QuestionType } from '@/types/domain';
import { AnswerOption, type AnswerState, answerLetter } from './AnswerOption';
import { CategoryChip } from './CategoryChip';

export interface QuestionCardProps {
  index?: number;
  total?: number;
  questionText: string;
  questionType: QuestionType;
  categoryName?: string | null;
  difficulty?: Difficulty;
  imagePath?: string | null;
  answers: { id: string; answer_text: string }[];
  selectedIds: string[];
  /** After answering (practice) or in review: reveal correctness. */
  correctIds?: string[] | null;
  locked?: boolean;
  onToggle?: (answerId: string) => void;
  headerExtra?: ReactNode;
  children?: ReactNode;
}

export function answerStateFor(id: string, selected: string[], correct?: string[] | null): AnswerState {
  const isSelected = selected.includes(id);
  if (!correct) return isSelected ? 'selected' : 'idle';
  const isCorrect = correct.includes(id);
  if (isSelected && isCorrect) return 'correct';
  if (isSelected && !isCorrect) return 'incorrect';
  if (!isSelected && isCorrect) return 'missed';
  return 'idle';
}

export function QuestionCard({
  index,
  total,
  questionText,
  questionType,
  categoryName,
  difficulty,
  imagePath,
  answers,
  selectedIds,
  correctIds,
  locked,
  onToggle,
  headerExtra,
  children,
}: QuestionCardProps) {
  const imageUrl = useSignedImage(imagePath);
  const multiple = questionType === 'multiple_response';
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap', gap: 1, '& > *': { m: '0 !important' } }}>
        {index != null && (
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Question {index + 1}
            {total ? ` of ${total}` : ''}
          </Typography>
        )}
        {categoryName !== undefined && <CategoryChip name={categoryName} />}
        {difficulty && <Chip size="small" label={difficulty} variant="outlined" sx={{ textTransform: 'capitalize' }} />}
        <Box sx={{ flex: 1 }} />
        {headerExtra}
      </Stack>
      <Typography component="h2" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 600, lineHeight: 1.5, mb: 2, whiteSpace: 'pre-line' }}>
        {questionText}
      </Typography>
      {imageUrl && (
        <Box component="img" src={imageUrl} alt="Question illustration" sx={{ maxWidth: '100%', maxHeight: 320, borderRadius: 2, mb: 2, display: 'block' }} />
      )}
      {multiple && !correctIds && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Select all that apply.
        </Typography>
      )}
      <Stack spacing={1.25} role={multiple ? 'group' : 'radiogroup'} aria-label="Answers">
        {answers.map((a, i) => (
          <AnswerOption
            key={a.id}
            label={answerLetter(i)}
            text={a.answer_text}
            multiple={multiple}
            state={answerStateFor(a.id, selectedIds, correctIds)}
            disabled={locked}
            onSelect={() => onToggle?.(a.id)}
          />
        ))}
      </Stack>
      {children}
    </Paper>
  );
}
